import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { SaveIcon, Trash2 } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { useCategories } from '@/api/category/use-categories.ts';
import { useDeleteTransfer } from '@/api/transfer/use-delete-transfer.ts';
import { useDeleteTransferRecord } from '@/api/transfer/use-delete-transfer-record.ts';
import { useTransfer } from '@/api/transfer/use-transfer.ts';
import { useTransferReceiptSuggestions } from '@/api/transfer/use-transfer-receipt-suggestions.ts';
import { useUpdateTransfer } from '@/api/transfer/use-update-transfer.ts';
import { type UpdateTransferDto } from '@/api/types';
import DrawerMobileClose from '@/components/drawer-mobile-close/drawer-mobile-close.tsx';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import AddPatternModal from '@/features/patterns/add-pattern-modal/add-pattern-modal.tsx';
import ManualSplitForm from '@/features/transfers/manual-split-form/manual-split-form.tsx';
import ReceiptSuggestionRow from '@/features/transfers/receipt-suggestion-row/receipt-suggestion-row.tsx';
import TransferRecordRow from '@/features/transfers/transfer-record-row/transfer-record-row.tsx';
import { formatPrice, parsePrice } from '@/helpers/formatters.ts';

const updateTransferSchema = z.object({
  rawParty: z.string().min(1, 'Party is required'),
  party: z.string().optional(),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .refine((value) => Number(value) > 0, 'Amount must be greater than 0'),
  date: z.string().min(1, 'Date is required'),
  description: z.string().optional(),
});

type UpdateTransferFormValues = {
  rawParty: string;
  party?: string;
  amount: string;
  date: string;
  description?: string;
};

type TransferDetailDrawerProps = {
  transferId: string;
};

const TransferDetailDrawer = ({ transferId }: TransferDetailDrawerProps) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [isManualSplitOpen, setIsManualSplitOpen] = useState(false);
  const [isPatternModalOpen, setIsPatternModalOpen] = useState(false);

  const form = useForm<UpdateTransferFormValues>({
    resolver: zodResolver(updateTransferSchema),
  });

  const { data: transfer, isLoading } = useTransfer(transferId);
  const { mutateAsync: updateTransfer, isPending: updateLoading } = useUpdateTransfer(transferId);
  const { mutateAsync: deleteTransfer, isPending: deleteLoading } = useDeleteTransfer();
  const { data: categories } = useCategories(projectId);
  const { data: suggestions } = useTransferReceiptSuggestions(transferId);
  const { mutate: deleteRecord } = useDeleteTransferRecord(transferId);

  useEffect(() => {
    if (!transfer) {
      return;
    }

    form.reset({
      rawParty: transfer.rawParty,
      party: transfer.party || '',
      amount: String(transfer.amount / 100),
      date: dayjs(transfer.date).format('YYYY-MM-DD'),
    });
  }, [transfer]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      navigate('..');
    }
  };

  const handleDelete = () => {
    if (confirm('Delete this transfer? This can only be undone in the database.')) {
      deleteTransfer(transferId).then(() => navigate('..'));
    }
  };

  const onSubmit = (data: UpdateTransferFormValues) => {
    const payload: UpdateTransferDto = {
      rawParty: data.rawParty,
      party: data.party || undefined,
      amount: parsePrice(Number(data.amount)),
      date: dayjs(data.date).toISOString(),
    };

    updateTransfer(payload).catch(() => undefined);
  };

  if (!transfer) {
    return (
      <Drawer direction="right" open onOpenChange={handleOpenChange}>
        <DrawerContent className="data-[vaul-drawer-direction=right]:w-full! data-[vaul-drawer-direction=right]:max-w-full! data-[vaul-drawer-direction=right]:sm:w-[600px]! data-[vaul-drawer-direction=right]:sm:max-w-[90vw]!">
          <div className="flex flex-col gap-4 p-4 overflow-y-auto">
            <div className="flex justify-end sm:hidden">
              <DrawerMobileClose />
            </div>
            {isLoading && (
              <div className="flex items-center justify-center h-48">
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            )}
            {!isLoading && (
              <div className="flex items-center justify-center h-48">
                <span className="text-sm text-muted-foreground">Transfer not found</span>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  const usedItemNames = new Set(transfer.records.flatMap((record) => record.items));
  const defaultRecord = transfer.records.find((record) => record.default);
  const canCreatePattern = !defaultRecord?.category && !!transfer.party;

  return (
    <Drawer direction="right" open onOpenChange={handleOpenChange}>
      <DrawerContent className="data-[vaul-drawer-direction=right]:w-full! data-[vaul-drawer-direction=right]:max-w-full! data-[vaul-drawer-direction=right]:sm:w-[600px]! data-[vaul-drawer-direction=right]:sm:max-w-[90vw]!">
        <FormProvider {...form}>
          <div className="flex flex-col gap-4 p-4 overflow-y-auto">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">Transfer Information</h2>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" disabled={deleteLoading} onClick={handleDelete}>
                  <Trash2 />
                  Delete
                </Button>
                <DrawerMobileClose />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">{formatPrice(transfer.amount)}</span>
            </div>
            <div className="flex flex-col gap-3">
              <FormInput
                label="Party (raw, as entered)"
                name="rawParty"
                disabled={!!transfer.extId}
              />
              <FormInput
                label="Party (normalized)"
                name="party"
                placeholder="Leave empty to use raw party"
              />
              <p className="text-xs text-muted-foreground -mt-2">
                Renaming this will update all transfers currently named "{transfer.party || '—'}"
                and remember this normalization for future transfers.
              </p>
              <div className="flex flex-col gap-3 sm:flex-row">
                <FormInput
                  label="Amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  disabled={!!transfer.extId}
                />
                <FormInput label="Date" name="date" type="date" disabled={!!transfer.extId} />
              </div>
              {form.formState.isDirty && (
                <Button disabled={updateLoading} onClick={form.handleSubmit(onSubmit)}>
                  <SaveIcon />
                  Save
                </Button>
              )}
            </div>
            <div className="w-full border-b border-muted-foreground/40 my-2" />
            <h2 className="text-lg font-semibold">Records</h2>
            <div className="flex flex-col gap-2">
              {transfer.records.map((record) => (
                <TransferRecordRow
                  key={`${record.uuid}-${record.updatedAt}`}
                  transferId={transferId}
                  record={record}
                  categories={categories}
                  onDelete={record.default ? undefined : deleteRecord}
                />
              ))}
            </div>
            {canCreatePattern && (
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => setIsPatternModalOpen(true)}
              >
                Create Pattern
              </Button>
            )}
            {!transfer.receipt &&
              !suggestions?.length &&
              (isManualSplitOpen ? (
                <ManualSplitForm
                  transferId={transferId}
                  onClose={() => setIsManualSplitOpen(false)}
                />
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="self-start"
                  onClick={() => setIsManualSplitOpen(true)}
                >
                  Split manually
                </Button>
              ))}
            {!!suggestions?.length && (
              <>
                <div className="w-full border-b border-muted-foreground/40 my-2" />
                <h2 className="text-lg font-semibold">
                  {transfer.receipt ? 'Assigned Receipt' : 'Matching Receipts'}
                </h2>
                <div className="flex flex-col gap-2">
                  {suggestions.map((receipt) => (
                    <ReceiptSuggestionRow
                      key={receipt.uuid}
                      transferId={transferId}
                      projectId={projectId}
                      receipt={receipt}
                      usedItemNames={usedItemNames}
                      autoExpand={suggestions.length === 1}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </FormProvider>
      </DrawerContent>
      {isPatternModalOpen && (
        <AddPatternModal
          onClose={() => setIsPatternModalOpen(false)}
          initialParty={transfer.party || undefined}
          initialAmount={transfer.amount}
        />
      )}
    </Drawer>
  );
};

export default TransferDetailDrawer;
